/**
 * Calcul du numéro de TVA intracommunautaire français.
 *
 * Le numéro FR est déterministe et se calcule localement à partir du SIREN
 * (aucun appel réseau nécessaire — l'annuaire officiel ne le fournit pas) :
 *
 *   TVA = "FR" + clé (2 chiffres) + SIREN (9 chiffres)
 *   clé = (12 + 3 × (SIREN mod 97)) mod 97
 *
 * Exemple : SIRET 753 108 900 00023 → SIREN 753108900 → FR42753108900
 */

/**
 * Calcule la TVA intracommunautaire FR à partir d'un SIRET (14 chiffres)
 * ou d'un SIREN (9 chiffres). Retourne le format canonique "FR42753108900",
 * ou null si l'entrée ne permet pas le calcul.
 */
export function computeFrenchVat(
  siretOrSiren: string | undefined | null,
): string | null {
  if (!siretOrSiren) return null
  const digits = String(siretOrSiren).replace(/\D/g, '')
  // Le SIREN correspond aux 9 premiers chiffres (le SIRET en compte 14).
  if (digits.length < 9) return null
  const siren = digits.slice(0, 9)
  const sirenNum = Number(siren)
  if (!Number.isFinite(sirenNum)) return null
  const key = (12 + 3 * (sirenNum % 97)) % 97
  const keyStr = String(key).padStart(2, '0')
  return `FR${keyStr}${siren}`
}

/**
 * Met en forme un numéro de TVA FR pour l'affichage : "FR 42 753 108 900".
 * Si l'entrée n'est pas un numéro FR reconnu, la retourne telle quelle.
 */
export function formatFrenchVat(vat: string | undefined | null): string {
  if (!vat) return ''
  const cleaned = String(vat).replace(/\s/g, '').toUpperCase()
  const m = /^FR(\d{2})(\d{9})$/.exec(cleaned)
  if (!m) return vat
  const [, key, siren] = m
  return `FR ${key} ${siren.slice(0, 3)} ${siren.slice(3, 6)} ${siren.slice(6, 9)}`
}
